import { useEffect, useState } from "react";
import { DeadLetterApi, DeadLetterApiError } from "../api/dead-letter.api";
import { DeadLetterDetail } from "../model/dead-letter";
import {
  FORBIDDEN_MESSAGE,
  UNAUTHORIZED_MESSAGE,
} from "./use-dead-letters-page";

interface UseDeadLetterDetail {
  detail: DeadLetterDetail | undefined;
  loading: boolean;
  error: string | undefined;
}

/**
 * The list endpoint omits payload and attributes to keep pages small, so the
 * detail is fetched only when a row is expanded.
 */
export const useDeadLetterDetail = (id: number): UseDeadLetterDetail => {
  const [detail, setDetail] = useState<DeadLetterDetail>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let active = true;
    setLoading(true);
    DeadLetterApi.getDeadLetter(id)
      .then((result) => {
        if (active) {
          setDetail(result);
          setError(undefined);
        }
      })
      .catch((e) => {
        if (!active) {
          return;
        }
        if (e instanceof DeadLetterApiError && e.unauthorized) {
          setError(UNAUTHORIZED_MESSAGE);
        } else if (e instanceof DeadLetterApiError && e.forbidden) {
          setError(FORBIDDEN_MESSAGE);
        } else if (e instanceof DeadLetterApiError && e.status === 404) {
          setError("This dead letter no longer exists.");
        } else {
          setError(
            `Could not load the dead letter: ${
              e instanceof Error ? e.message : "unexpected error"
            }`
          );
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [id]);

  return { detail, loading, error };
};
